import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye, EyeOff, Smartphone } from 'lucide-react';
import {
  listCustomerDevices,
  maskImei,
  type CustomerDeviceHistoryItem,
} from '@/lib/api/customers.js';
import { formatDate } from '@/lib/format.js';
import { cn } from '@/lib/utils.js';

export type SelectedCustomerDevice = CustomerDeviceHistoryItem;

type CustomerPreviousDevicesPanelProps = {
  customerId: string | null | undefined;
  customerFirstName?: string | null;
  /** Exclude devices already on the ticket (by modelId or makeId+custom name). */
  excludeKeys?: string[];
  onSelect: (device: CustomerDeviceHistoryItem) => void;
  onNewDevice: () => void;
  className?: string;
};

function deviceKey(d: CustomerDeviceHistoryItem): string {
  return [d.makeId ?? 'null', d.modelId ?? 'null', (d.customModelName ?? '').trim().toLowerCase()].join(
    '|',
  );
}

function MaskedImei({ imei }: { imei: string }) {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!revealed) return;
    const t = window.setTimeout(() => setRevealed(false), 10_000);
    return () => window.clearTimeout(t);
  }, [revealed]);

  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 font-mono text-xs text-slate-500 hover:text-slate-800"
      onClick={() => setRevealed((v) => !v)}
      title={revealed ? 'Hide IMEI' : 'Reveal IMEI'}
    >
      IMEI: {revealed ? imei : maskImei(imei)}
      {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
    </button>
  );
}

export function CustomerPreviousDevicesPanel({
  customerId,
  customerFirstName,
  excludeKeys = [],
  onSelect,
  onNewDevice,
  className,
}: CustomerPreviousDevicesPanelProps) {
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const devicesQuery = useQuery({
    queryKey: ['customer-devices', customerId],
    queryFn: () => listCustomerDevices(customerId!),
    enabled: Boolean(customerId),
    staleTime: 60_000,
  });

  const devices = useMemo(() => {
    const list = (devicesQuery.data ?? []).filter((d) => !excludeKeys.includes(deviceKey(d)));
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (d) =>
        d.displayName.toLowerCase().includes(q) ||
        (d.lastRepairSummary ?? '').toLowerCase().includes(q) ||
        (d.lastImei ?? '').includes(q),
    );
  }, [devicesQuery.data, excludeKeys, search]);

  if (!customerId) return null;
  if (devicesQuery.isError) return null;
  if (devicesQuery.isSuccess && (devicesQuery.data?.length ?? 0) === 0) return null;

  const label = customerFirstName
    ? `${customerFirstName.toUpperCase()}'S DEVICES`
    : 'PREVIOUS DEVICES';

  const limit = 5;
  const needsTruncate = devices.length >= 8 && !showAll;
  const visible = needsTruncate ? devices.slice(0, limit) : devices;
  const showSearch = (devicesQuery.data?.length ?? 0) >= 4;

  return (
    <div className={cn('space-y-3', className)}>
      <div className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</div>

      {devicesQuery.isLoading ? (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      ) : (
        <>
          {showSearch ? (
            <input
              type="search"
              placeholder="Search devices..."
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          ) : null}

          <div className="space-y-2">
            {visible.map((device) => (
              <div
                key={deviceKey(device)}
                className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4"
              >
                <Smartphone className="h-8 w-8 shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-950">{device.displayName}</div>
                  <div className="text-sm text-slate-500">
                    Last repaired: {device.lastRepairSummary}
                    {device.lastTicketDate
                      ? ` · ${formatDate(device.lastTicketDate.slice(0, 10))}`
                      : ''}
                  </div>
                  {device.lastImei ? <MaskedImei imei={device.lastImei} /> : null}
                  <div className="text-xs text-slate-400">
                    {device.totalRepairs} previous repair{device.totalRepairs === 1 ? '' : 's'}
                  </div>
                </div>
                <button
                  type="button"
                  className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white"
                  onClick={() => onSelect(device)}
                >
                  Use This Device
                </button>
              </div>
            ))}
          </div>

          {needsTruncate ? (
            <button
              type="button"
              className="text-sm font-semibold text-primary"
              onClick={() => setShowAll(true)}
            >
              Show all {devices.length} devices ▼
            </button>
          ) : null}
          {showAll && devices.length >= 8 ? (
            <button
              type="button"
              className="text-sm font-semibold text-slate-500"
              onClick={() => setShowAll(false)}
            >
              Show fewer ▲
            </button>
          ) : null}
        </>
      )}

      <button
        type="button"
        className="w-full rounded-xl border border-dashed border-slate-300 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        onClick={onNewDevice}
      >
        + Different Device / New Device
      </button>
    </div>
  );
}

export { deviceKey as customerDeviceKey };
